import { useState, useEffect } from 'react';
import * as THREE from 'three';
import { Project } from '../../types';
import { useAppStore } from '../../store/useAppStore';
// Cache global pour les médias
export const videoCache = new Map<string, HTMLVideoElement>();
export const textureCache = new Map<string, THREE.Texture>();
const mediaLoadPromises = new Map<string, Promise<void>>();
const loadedUrls = new Set<string>();

let mediaLoadedCache = false;
const textureLoader = new THREE.TextureLoader();

/** Réinitialise tous les caches (textures, videos, promises). À appeler au démontage de Home. */
export function resetMediaCache(): void {
  textureCache.forEach(t => t.dispose());
  textureCache.clear();
  videoCache.forEach(v => {
    v.pause();
    v.src = '';
  });
  videoCache.clear();
  loadedUrls.clear();
  mediaLoadPromises.clear();
  mediaLoadedCache = false;
}
const MEDIA_LOAD_TIMEOUT_MS = 10000;

// Fonction de préchargement global
export async function preloadMedia(url: string): Promise<void> {
  if (loadedUrls.has(url)) return;

  let loadPromise = mediaLoadPromises.get(url);
  if (loadPromise) {
    await loadPromise;
    return;
  }

  if (url.endsWith('.mp4') || url.endsWith('.webm')) {
    loadPromise = loadVideo(url);
  } else {
    loadPromise = loadTexture(url);
  }

  mediaLoadPromises.set(url, loadPromise);
  try {
    await loadPromise;
  } catch (error) {
    mediaLoadPromises.delete(url);
    throw error;
  }
}

const loadTexture = async (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (textureCache.has(url)) {
      resolve();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      reject(new Error(`Texture load timeout: ${url}`));
    }, MEDIA_LOAD_TIMEOUT_MS);

    textureLoader.load(
      url,
      texture => {
        window.clearTimeout(timeoutId);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = true;
        texture.anisotropy = 4;
        textureCache.set(url, texture);
        loadedUrls.add(url);
        resolve();
      },
      undefined,
      error => {
        window.clearTimeout(timeoutId);
        console.error(`Failed to load texture: ${url}`, error);
        reject(error);
      }
    );
  });
};

const loadVideo = async (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (videoCache.has(url)) {
      resolve();
      return;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.loop = true;
    video.crossOrigin = 'anonymous';

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      video.removeEventListener('loadeddata', handleLoad);
      video.removeEventListener('canplay', handleLoad);
      video.removeEventListener('error', handleError);
      video.removeEventListener('stalled', handleError);
      video.removeEventListener('abort', handleError);
    };

    const handleLoad = () => {
      cleanup();
      videoCache.set(url, video);
      loadedUrls.add(url);
      resolve();
    };

    const handleError = () => {
      cleanup();
      reject(new Error(`Failed to load video: ${url}`));
    };

    const timeoutId = window.setTimeout(() => {
      handleError();
    }, MEDIA_LOAD_TIMEOUT_MS);

    // `canplaythrough` is unreliable for large/compressed assets and can hang silently.
    // `loadeddata` / `canplay` are enough for our preload use case.
    video.addEventListener('loadeddata', handleLoad);
    video.addEventListener('canplay', handleLoad);
    video.addEventListener('error', handleError);
    video.addEventListener('stalled', handleError);
    video.addEventListener('abort', handleError);
    video.src = url;
    video.load();
  });
};

export function useMediaLoader(projects: Project[]) {
  const [allMediaLoaded, setAllMediaLoaded] = useState(mediaLoadedCache);
  const { setLoadingProgress, setMediaNeedsReload } = useAppStore();
  const mediaNeedsReload = useAppStore(state => state.mediaNeedsReload);
  const selectedProject = useAppStore(state => state.selectedProject);

  // 1. Chargement initial : thumbnails seulement (lazy projectMedia)
  useEffect(() => {
    if (mediaLoadedCache && !mediaNeedsReload) return;
    if (mediaNeedsReload) {
      setMediaNeedsReload(false);
      setAllMediaLoaded(false);
    }

    const thumbnails = projects.map(p => p.thumbnail);
    const total = thumbnails.length;
    let loaded = 0;

    const loadThumbnails = async () => {
      const results = await Promise.allSettled(
        thumbnails.map(async url => {
          try {
            await preloadMedia(url);
          } finally {
            loaded++;
            setLoadingProgress((loaded / total) * 100);
          }
        })
      );

      const failedLoads = results.filter(
        result => result.status === 'rejected'
      ) as PromiseRejectedResult[];

      if (failedLoads.length > 0) {
        console.warn(
          `Some media failed to preload (${failedLoads.length}/${total}). Continuing.`,
          failedLoads.map(result => result.reason)
        );
      }

      mediaLoadedCache = true;
      setAllMediaLoaded(true);
      setLoadingProgress(100);
    };

    loadThumbnails();
  }, [projects, setLoadingProgress, setMediaNeedsReload, mediaNeedsReload]);

  // 2. Lazy projectMedia : charger quand un projet est sélectionné
  useEffect(() => {
    if (!selectedProject?.projectMedia?.length) return;

    const loadProjectMedia = async () => {
      await Promise.allSettled(
        selectedProject.projectMedia!.map(url => preloadMedia(url))
      );
    };

    loadProjectMedia();
  }, [selectedProject?.id]);

  return { allMediaLoaded };
}
